import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  FileText, 
  FolderOpen, 
  Building2, 
  Users, 
  Settings, 
  LogOut, 
  ChevronLeft, 
  ChevronRight,
  Plus,
  MessageSquare,
  Search,
  FolderArchive,
  UserCircle,
  Receipt,
  Brain,
  Calendar,
  Bell,
  Shield,
  Scale
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navSections = [
  {
    title: null,
    items: [
      { icon: LayoutDashboard, label: "Tableau de bord", href: "/dashboard" },
      { icon: Search, label: "Recherche", href: "/search" },
    ]
  },
  {
    title: "Documents",
    items: [
      { icon: FolderOpen, label: "Modèles", href: "/templates" },
      { icon: FileText, label: "Contrats", href: "/contracts" },
      { icon: FolderArchive, label: "Documents", href: "/documents" },
    ]
  },
  {
    title: "Intelligence Artificielle",
    items: [
      { icon: MessageSquare, label: "Assistant IA", href: "/ai/chat" },
      { icon: Brain, label: "Analyse IA", href: "/ai/analysis" },
      { icon: Scale, label: "Consultation", href: "/consultation" },
      { icon: Shield, label: "Surveillance", href: "/surveillance" },
    ]
  },
  {
    title: "Cabinet",
    items: [
      { icon: UserCircle, label: "Clients", href: "/clients" },
      { icon: FolderArchive, label: "Dossiers", href: "/cases" },
      { icon: Calendar, label: "Calendrier", href: "/calendar" },
      { icon: Receipt, label: "Facturation", href: "/billing" },
    ]
  },
  {
    title: "Administration",
    items: [
      { icon: Building2, label: "Organisation", href: "/organization", adminOnly: true },
      { icon: Users, label: "Utilisateurs", href: "/users", adminOnly: true },
    ]
  },
];

function Sidebar(props) {
  const user = props.user;
  const collapsed = props.collapsed;
  const setCollapsed = props.setCollapsed;
  
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async function() {
    try {
      await fetch(API_URL + "/api/auth/logout", {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('auth_token');
    navigate('/login');
  };

  const getInitials = function(name) {
    if (!name) return "U";
    const parts = name.split(' ');
    let result = "";
    for (let i = 0; i < parts.length && i < 2; i++) {
      if (parts[i] && parts[i][0]) {
        result += parts[i][0].toUpperCase();
      }
    }
    return result || "U";
  };

  const getRoleName = function(role) {
    const roles = {
      admin: "Administrateur",
      lawyer: "Avocat",
      jurist: "Juriste",
      assistant: "Assistant juridique",
      user: "Utilisateur"
    };
    return roles[role] || "Utilisateur";
  };

  return (
    <aside 
      data-testid="sidebar"
      className={cn(
        "sidebar h-screen flex flex-col transition-sidebar",
        collapsed ? "sidebar-collapsed" : "sidebar-expanded"
      )}
    >
      <div className="h-14 flex items-center justify-between px-4 border-b border-blue-100">
        {!collapsed && (
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-semibold text-lg text-blue-900">Lexora LegalTech</span>
          </Link>
        )}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={function() { setCollapsed(!collapsed); }}
          className="ml-auto h-8 w-8"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>

      {!collapsed && (
        <div className="p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="w-full justify-start gap-2 h-9" size="sm" data-testid="new-action-btn">
                <Plus className="w-4 h-4" />
                Nouveau
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuItem onClick={function() { navigate('/templates/upload'); }} data-testid="new-template-btn">
                <FolderOpen className="w-4 h-4 mr-2" />
                Nouveau modèle
              </DropdownMenuItem>
              <DropdownMenuItem onClick={function() { navigate('/templates'); }} data-testid="new-contract-btn">
                <FileText className="w-4 h-4 mr-2" />
                Nouveau contrat
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={function() { navigate('/ai/chat'); }} data-testid="new-ai-chat-btn">
                <MessageSquare className="w-4 h-4 mr-2" />
                Conversation IA
              </DropdownMenuItem>
              <DropdownMenuItem onClick={function() { navigate('/ai/analysis'); }} data-testid="new-ai-analysis-btn">
                <Brain className="w-4 h-4 mr-2" />
                Analyse IA
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-4">
        {navSections.map(function(section, sIdx) {
          const visibleItems = section.items.filter(function(item) {
            if (item.adminOnly && user?.role !== 'admin') return false;
            return true;
          });
          if (visibleItems.length === 0) return null;

          return (
            <div key={sIdx}>
              {!collapsed && section.title && (
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {section.title}
                </p>
              )}
              <div className="space-y-0.5">
                {visibleItems.map(function(item) {
                  const isActive = location.pathname === item.href || 
                    (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      data-testid={"nav-" + item.href.replace(/\//g, '-').slice(1)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-1.5 rounded-md transition-colors text-sm",
                        isActive 
                          ? "bg-blue-600 text-white shadow-sm" 
                          : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                      )}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {!collapsed && <span className="font-medium">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="p-3 border-t border-blue-100">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className={cn(
                "flex items-center gap-3 w-full p-2 rounded-md hover:bg-accent transition-colors",
                collapsed && "justify-center"
              )}
              data-testid="user-menu-trigger"
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
              <p className="text-xs text-muted-foreground capitalize mt-1">
                {getRoleName(user?.role)}
              </p>
            </div>
            <DropdownMenuSeparator />
            {user?.role === 'admin' && (
              <DropdownMenuItem onClick={function() { navigate('/organization'); }} data-testid="user-settings">
                <Settings className="w-4 h-4 mr-2" />
                Paramètres
              </DropdownMenuItem>
            )}
            {user?.role === 'admin' && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}

export { Sidebar };
export default Sidebar;
